// The Product Metric is the "industry-native size" of a deal, and it is
// NOT the same noun in every company (CLAUDE.md "Value model (three-field)"):
// Financial thinks in AUM, Insurance/Planning in Annual Premium, Mortgage
// in Loan Amount, Realty in Sale Price. Asking a Financial advisor for a
// generic "Product Metric" reads as jargon — the whole point of Stewardship
// OS is to speak each company's own language (the thesis: "terminology
// matches Stewardship's language").
//
// This is the single source of truth for that label. It was previously a
// private function inside OpportunityDetailPage; it now lives here so the
// Data Needed "Processing numbers" editor, the opportunity detail page, and
// the transaction detail page all say the same thing.
//
// Pure (no DB / no React imports) so both the web bundle and the workers
// runtime can use it.

/**
 * The company-native label for a deal's Product Metric.
 *
 * @param companySlug the owning company ("financial" | "insurance" |
 *   "planning" | "mortgage" | "realty"), or null when unknown.
 * @param unit the optional `product_metric_unit` code. `premium_annual`
 *   forces "Annual Premium" regardless of company (a premium-denominated
 *   record is a premium record).
 */
export function productMetricLabel(
  companySlug: string | null,
  unit: string | null = null,
): string {
  if (unit === "premium_annual") return "Annual Premium";
  switch (companySlug) {
    case "financial":
      return "AUM";
    case "insurance":
    case "planning":
      return "Annual Premium";
    case "mortgage":
      return "Loan Amount";
    case "realty":
      return "Sale Price";
    default:
      return "Product Metric";
  }
}

/**
 * The company-native label for a record's Year-1 revenue field. Realty
 * calls it the commission, because that is the word Nate uses ("this
 * probably just means the commission", 2026-07-02).
 *
 * @param suffix appended when set — e.g. " ($)" for a form field label.
 */
export function revenueFieldLabel(
  companySlug: string | null,
  suffix = "",
): string {
  const base =
    companySlug === "realty"
      ? "Year-1 revenue — your commission"
      : "Year-1 revenue";
  return `${base}${suffix}`;
}

// The generic rule, unchanged, for every company but Realty: the number is
// Stewardship's gross fee/commission, before the cost of DELIVERING the
// work (document services, overhead) — those belong in profitability.
const REVENUE_HELP_GENERIC =
  "Gross dollars Stewardship earns from this record in year one — the full fee or commission. Don't subtract delivery or overhead costs (e.g. document services); those show up in profitability, not here.";

// Realty's revenue definition changed twice on 2026-07-29, both times on
// Grant's call. First (v1.136): a client rebate/gift comes OUT of the
// number — it's net, per comp-tables-v0.2's "Net Revenue to Stewardship".
// Then, hours later, Grant retired the derive-it-from-the-10%-rebate
// framing entirely: closed Realty deals are recorded as THE THREE METRICS
// BROKERMINT REPORTS (Office gross / Agent net / Office net), verbatim,
// so entry is a copy-across instead of arithmetic — and any future change
// to which one feeds the rollup is a recompute, never a re-ask.
//
// The revenue rollup figure is OFFICE NET. That mapping isn't new: the
// original realty schema stores BrokerMint's office_net in a column
// literally named `stewardship_net` (migration 0028 keeps them paired),
// and the comp doc designates the net figure as the P&L / founder-
// dashboard number. Office net is net of the agent split and of anything
// given back to the client — so v1.136's "after the gift" answer to Nate
// still holds; he just copies the number instead of computing it.
const REVENUE_HELP_REALTY =
  "Stewardship's revenue on this deal — BrokerMint's Office net (what the office kept after the agent split and any rebate or gift back to the client). On a closed deal, copy it straight from BrokerMint; before close, enter your best estimate.";

/**
 * Plain-English "what is this number and why am I typing it" help for the
 * Year-1 revenue field, in the company's own terms.
 */
export function revenueHelp(companySlug: string | null): string {
  return companySlug === "realty" ? REVENUE_HELP_REALTY : REVENUE_HELP_GENERIC;
}

// ─── Realty commission breakdown (Grant, 2026-07-29) ─────────────────
//
// "Move from less the 10% to the three metrics BrokerMint gives us."
// A closed Realty deal's commission is captured as BrokerMint's own three
// numbers, labeled with BrokerMint's own column names so an advisor with
// BrokerMint open in the next tab copies straight across. Stored on the
// linked property record (realty_listings / realty_purchases — the same
// columns the BrokerMint importer fills), never duplicated onto the
// transaction row; the transaction's estimated_revenue_year_1 carries
// Office net, its product_metric carries the sale price.

export type RealtyCommissionMetricKey =
  | "office_gross"
  | "agent_net"
  | "office_net";

export interface RealtyCommissionMetricDef {
  key: RealtyCommissionMetricKey;
  /** Field label — BrokerMint's name, so entry is a copy-across. */
  label: string;
  help: string;
}

export const REALTY_COMMISSION_METRICS: readonly RealtyCommissionMetricDef[] =
  [
    {
      key: "office_gross",
      label: "Office gross",
      help: "The full commission on the deal — BrokerMint's Office gross column, copied as-is.",
    },
    {
      key: "agent_net",
      label: "Agent net",
      help: "The agent's take-home on the deal — BrokerMint's Agent net column, copied as-is.",
    },
    {
      key: "office_net",
      label: "Office net — Stewardship's revenue",
      help: "What the office kept — BrokerMint's Office net column, copied as-is. This is the deal's Year-1 revenue figure on dashboards and rollups.",
    },
  ];

/**
 * Plain-English "what is this number and why am I typing it" help for the
 * Product Metric field, in the company's own terms. Always ends with the
 * "context only — this is NOT Stewardship's revenue" clarification, because
 * the #1 confusion (Nate, Ryan) is thinking the metric IS the commission.
 */
export function productMetricHelp(companySlug: string | null): string {
  const NOT_REVENUE = "Context only — this is NOT Stewardship's revenue.";
  switch (companySlug) {
    case "financial":
      return `The account value / assets under management (AUM). ${NOT_REVENUE}`;
    case "insurance":
    case "planning":
      return `The annual premium on the policy. ${NOT_REVENUE}`;
    case "mortgage":
      return `The loan / origination amount. ${NOT_REVENUE}`;
    case "realty":
      return `The sale price. ${NOT_REVENUE}`;
    default:
      return `The industry-native size of the record (origination amount, AUM, premium, sale price). ${NOT_REVENUE}`;
  }
}
